import React from "react"
import {
  Menu,
  MenuItems,
  MenuPopover,
  MenuButton,
  MenuItem
} from "@reach/menu-button"
import "@reach/menu-button/styles.css"
import Toolbar from "./toolbar"
import ExperimentalToolbar from "./experimental-toolbar"
import { useEditor, useValue } from "../../editor-context"
import { useIcon } from "../../icon-context"
import Portal from "@kit/design/portal"
import ColorPicker from "@kit/design/color-picker"
import { OldIconButton } from "@kit/design/old-button"
import { useSavedColors } from "hooks/use-saved-colors"
import "./index.css"

const createMarkButton = (mark, iconProps) => () => {
  const MarkIcon = useIcon(mark)
  const editor = useEditor()

  const toggleMark = event => {
    event.preventDefault()
    editor.toggleMark(mark)
  }

  return (
    <button
      className="convertkit-editor-selection-toolbar-button"
      type="button"
      onMouseDown={toggleMark}
      aria-label={`toggle ${mark}`}
    >
      <MarkIcon {...iconProps} />
    </button>
  )
}

const alignments = ["left", "center", "right", "justify"]

const ColorMenu = ({ close, editor }) => {
  const handleChange = color => {
    editor.addColor({ color: color.hex })
  }

  const color = editor.query("getColor")
  const savedColors = useSavedColors()

  return (
    <ColorPicker
      active
      variant="dark"
      position="relative"
      onClose={close}
      color={color}
      onChange={handleChange}
      style={{ width: 200, top: -240 }}
      savedColors={savedColors}
    />
  )
}

const ColorButton = {
  component: ({ menu }) => (
    <OldIconButton
      className="convertkit-editor-selection-toolbar-button"
      icon="drop"
      onClick={menu.open}
      label="Color"
    />
  ),
  menu: ColorMenu,
  onClick(event, editor, menu) {
    menu()
  }
}

const AlignmentButton = () => {
  const icons = [
    useIcon("alignLeft"),
    useIcon("alignCenter"),
    useIcon("alignRight"),
    useIcon("alignJustify")
  ]
  const editor = useEditor()
  const value = useValue()

  const startBlock = value.startBlock
  const current = (startBlock && startBlock.data.get("alignment")) || "left"
  const next = alignments[(alignments.indexOf(current) + 1) % alignments.length]

  const toggleAlignment = event => {
    event.preventDefault()
    editor.setBlocks({ data: { alignment: next } })
  }

  const index = alignments.indexOf(current)

  const AlignmentIcon = icons[index]

  return (
    <button
      className="convertkit-editor-selection-toolbar-button"
      type="button"
      onMouseDown={toggleAlignment}
      aria-label={`align text ${next}`}
    >
      <AlignmentIcon />
    </button>
  )
}

const setFontSize = (editor, value) => () => {
  editor.setFontSize(value)
  editor.focus()
}

const FontSize = () => {
  const FontSizeIcon = useIcon("fontSize")
  const editor = useEditor()

  return (
    <div className="inline-block relative">
      <Menu onChange={setFontSize}>
        <MenuButton className="convertkit-editor-selection-toolbar-button">
          <FontSizeIcon />
        </MenuButton>
        <MenuPopover style={{ padding: 0 }} portal={false}>
          <MenuItems
            className="font-size-list"
            style={{
              height: "100px",
              overflowY: "scroll",
              background: "rgb(64, 78, 92)",
              color: "#fff",
              border: "none",
              borderRadius: "2px",
              padding: 0
            }}
          >
            <MenuItem onSelect={setFontSize(editor, 8)}>8</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 9)}>9</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 10)}>10</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 11)}>11</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 12)}>12</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 14)}>14</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 16)}>16</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 18)}>18</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 24)}>24</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 32)}>32</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 48)}>48</MenuItem>
            <MenuItem onSelect={setFontSize(editor, 72)}>72</MenuItem>
          </MenuItems>
        </MenuPopover>
      </Menu>
    </div>
  )
}

const buttonDefaults = {
  bold: createMarkButton("bold"),
  italic: createMarkButton("italic"),
  underline: createMarkButton("underline", { transform: "down-1" }),
  strikethrough: createMarkButton("strikethrough"),
  "font-size": FontSize,
  alignment: AlignmentButton,
  color: ColorButton
}

const defaults = [
  // "bold",
  // "italic",
  // "underline",
  // "strikethrough",
  // "font-size",
  // "alignment"
]

function SelectionToolbar({ buttons = defaults, exclude = [] }) {
  const editor = useEditor()
  const buttonComponents = buttons.map(button => {
    if (button.component) return button
    const buttonConfigOrComponent = buttonDefaults[button]
    if (buttonConfigOrComponent.component) return buttonConfigOrComponent
    return { component: buttonConfigOrComponent }
  })

  const Component = editor?.props?.experimentalToolbar
    ? ExperimentalToolbar
    : Toolbar

  return (
    <Portal>
      <Component buttons={buttonComponents} exclude={exclude} />
    </Portal>
  )
}

export default SelectionToolbar
